import axios, { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import useSWR from 'swr';

import { getCompanySlug } from './getCompanySlug';

import authService from '../../services/authService';
import {
  triggerErrorModal,
  triggerUnexpectedErrorModal,
} from '../../services/errorModalService';

import { getCurrentLanguage } from '~/contexts/lang/languageContext';
import { authMutex } from '~/services/api/auth-mutex';
import { isStandardError } from '~/services/api/rest-api-error';
import { store } from '~/utils/store';

interface ErrorResponse {
  message: string;
}

const refreshAccessToken = async () => {
  try {
    const response = await authService.refreshAccessToken();
    if (response) {
      return response;
    } else {
      throw new Error('Something went wrong.');
    }
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    throw axiosError;
  }
};

const API_PATH = import.meta.env.VITE_API_PATH ?? '';
const HTTPS_PROTOCOL = import.meta.env.VITE_HTTPS_PROTOCOL;
const WB_URL =
  import.meta.env.VITE_NODE_ENV === 'dev'
    ? import.meta.env.VITE_DEV_FALLBACK_URL
    : window.location.hostname;
const WB_COMPANY_SLUG = getCompanySlug(WB_URL);

const createAxiosInstance = (
  baseURL: string,
  hasAuth: boolean = true,
  hasErrorModal: boolean = true,
) => {
  // Create Axios instance with common configuration
  const instance = axios.create({ baseURL, withCredentials: hasAuth });
  const region = store.getState().auth.user.region;

  // Add an interceptor/middleware to handle 401 errors
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response &&
        error.response.status === StatusCodes.UNAUTHORIZED &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (authMutex.isLocked()) {
          await authMutex.waitForUnlock();
          return instance(originalRequest);
        }

        const release = await authMutex.acquire();
        try {
          const refreshTokenResponse = await refreshAccessToken();
          localStorage.setItem(
            'user_type',
            String(refreshTokenResponse.user_type),
          );
          localStorage.setItem(
            'refresh_expiration',
            refreshTokenResponse.refresh_expiration,
          );
          localStorage.setItem(
            'access_expiration',
            refreshTokenResponse.access_expiration,
          );
          return instance(originalRequest);
        } catch {
          const isSecurePortal =
            window.location.pathname.includes('/portal/request-for-information');
          if (!isSecurePortal) {
            window.location.href = '/logout';
          }
          return Promise.reject(error);
        } finally {
          release();
        }
      }

      if (isStandardError(error.response?.data)) {
        const hasUnexpectedCode = error.response.data.errors.some(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (errorObj: any) => errorObj.code === 'unexpected',
        );
        const serverError =
          error.response.status >= StatusCodes.INTERNAL_SERVER_ERROR;
        const isUnexpected = hasUnexpectedCode || serverError;
        const notAccessError =
          error.response.status !== StatusCodes.UNAUTHORIZED &&
          error.response.status !== StatusCodes.FORBIDDEN;

        if (hasErrorModal && notAccessError && isUnexpected) {
          triggerUnexpectedErrorModal(error);
        } else {
          triggerErrorModal(error);
        }
      } else {
        triggerUnexpectedErrorModal(error);
      }
      throw error;
    },
  );

  // Add an interceptor to each request to disable caching
  instance.interceptors.request.use(
    (config) => {
      config.headers['Cache-Control'] = 'no-cache'; // or 'no-store' for more strict caching policy
      // Add custom header
      config.headers['X-Frontend-Auth'] = import.meta.env.VITE_X_FRONTEND_AUTH;
      config.headers.Region = region ?? '';
      const currentLanguage = getCurrentLanguage();
      config.headers['Accept-Confide-Language'] = currentLanguage.code;
      return config;
    },
    async (error) => {
      triggerUnexpectedErrorModal(error);
      throw error;
    },
  );

  return instance;
};

// baseurl with no slug
const axiosInstance = () => createAxiosInstance(`${HTTPS_PROTOCOL}${API_PATH}`);
const axiosInstanceForPublicAPI = () =>
  createAxiosInstance(`${HTTPS_PROTOCOL}${API_PATH}`, false);
// baseurl with tenant and slug
const axiosInstanceForTenantAPI = (slug?: string | null) =>
  createAxiosInstance(
    `${HTTPS_PROTOCOL}${API_PATH}/tenant/${
      slug ?? localStorage.getItem('slug')
    }`,
  );
// baseurl with slug only
const axiosInstanceWithSlug = () =>
  createAxiosInstance(
    `${HTTPS_PROTOCOL}${API_PATH}/${
      localStorage.getItem('slug') ?? WB_COMPANY_SLUG
    }`,
  );

const axiosInstanceWithSlugForPublicAPI = () =>
  createAxiosInstance(
    `${HTTPS_PROTOCOL}${API_PATH}/${
      WB_COMPANY_SLUG ?? localStorage.getItem('slug')
    }`,
    false,
  );

// baseurl with slug only for internal submissions
const axiosInstanceForTenantSlugOnly = () =>
  createAxiosInstance(
    `${HTTPS_PROTOCOL}${API_PATH}/${localStorage.getItem('slug')}`,
  );

// useSWR with slug
const useSWRWithoutSlug = (url: string) =>
  useSWR(url ?? '', {
    revalidateOnFocus: true,
  });

// useSWR with slug
const useSWRWithSlug = (url: string) =>
  useSWR(
    `${localStorage.getItem('slug') ?? WB_COMPANY_SLUG}`
      ? `${localStorage.getItem('slug') ?? WB_COMPANY_SLUG}${url}`
      : '',
    {
      revalidateOnFocus: true,
    },
  );

// useSWR for tenant with slug
const useSWRForTenantAPIWithSlug = (url: string) =>
  useSWR(
    `${localStorage.getItem('slug') ?? WB_COMPANY_SLUG}`
      ? `/tenant/${localStorage.getItem('slug') ?? WB_COMPANY_SLUG}${url}`
      : '',
    {
      revalidateOnFocus: true,
    },
  );

// added a optional parameter to have the ability to set the auth to be disabled
const axiosInstanceForPublicAPIWithAuth = (hasAuth: boolean = true) =>
  createAxiosInstance(`${HTTPS_PROTOCOL}${API_PATH}`, hasAuth);

// override error framework for endpoint used as checker (which usually returns 400 when not valid) eg. email checker
const axiosInstanceWithoutErrorModal = () =>
  createAxiosInstance(`${HTTPS_PROTOCOL}${API_PATH}`, true, false);
const axiosInstanceForTenantOnly = () =>
  createAxiosInstance(`${HTTPS_PROTOCOL}${API_PATH}/tenant`);

export {
  WB_COMPANY_SLUG,
  useSWRWithSlug,
  axiosInstance,
  axiosInstanceForPublicAPI,
  axiosInstanceForTenantAPI,
  useSWRWithoutSlug,
  axiosInstanceWithSlug,
  axiosInstanceForTenantSlugOnly,
  axiosInstanceWithoutErrorModal,
  axiosInstanceForTenantOnly,
  axiosInstanceWithSlugForPublicAPI,
  axiosInstanceForPublicAPIWithAuth,
  useSWRForTenantAPIWithSlug,
};
